/**
 * `useDownloadLink` is used to attach a download link
 * to the DOM in order to download a document. This is
 * accomplished by creating a new anchor tag element which
 * is then given the attributes from `downloadLinkAttributes`.
 *
 * Example:
 *  ```js
 * export function useDownloadContract() {
 *    const { setDownloadData } = useDownloadLink()
 *
 *    // make call to retrieve document and
 *    // set downloadData if call is successful
 *    const downloadFunc = async () => {
 *        // ...your async code here...
 *        if (success) {
 *          setDownloadData(data)
 *        }
 *    }
 * }
 *  ```
 *
 * @returns setDownloadData
 */
export function useDownloadLink() {
    const setDownloadData = (downloadData, options) => {
        const { mimeType = 'application/pdf', addHead = true, openNewTab = false, } = options ?? {};
        if (downloadData?.filename &&
            downloadData?.filename.length > 0 &&
            downloadData?.data &&
            downloadData?.data?.length > 0) {
            const href = `${addHead ? `data:${mimeType};base64,` : ''}${downloadData?.data}`;
            if (openNewTab) {
                const pdfWindow = window.open('');
                if (pdfWindow) {
                    pdfWindow.document.write(`<html><head><title>${downloadData?.filename ?? ''}</title></head><body><iframe width='100%' height='100%' src='${encodeURI(href)}'></iframe>`);
                }
            }
            else {
                const downloadLinkAttributes = {
                    href,
                    target: '_blank',
                    download: downloadData?.filename ?? '',
                };
                const node = document.createElement('a');
                Object.keys(downloadLinkAttributes).forEach((key) => {
                    const attribute = document.createAttribute(key);
                    attribute.value = downloadLinkAttributes[key];
                    node.setAttributeNode(attribute);
                });
                node.click();
                node.remove();
            }
        }
    };
    return { setDownloadData };
}
